import { api } from "./axios";

interface Envelope<T> {
  success: true;
  data: T;
  meta?: { page: number; limit: number; total: number };
}

export interface ActiveEmployee {
  id: string;
  fullName: string;
  designation: string | null;
  status: "ACTIVE" | "INACTIVE" | "ON_LEAVE";
}

export interface Employee {
  id: string;
  fullName: string;
  phone: string;
  designation: string | null;
  status: "ACTIVE" | "INACTIVE" | "ON_LEAVE";
  performanceScore: number;
  activeJobs: number;
  completedJobs: number;
  user: { email: string; isActive: boolean; lastLoginAt: string | null };
  createdAt: string;
}

export interface EmployeeJob {
  id: string;
  complaintNumber: string;
  productName: string;
  status: string;
  priority: string;
  createdAt: string;
  expectedCompletionDate: string | null;
  customer: { id: string; fullName: string; phone: string };
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface CreateEmployeeInput {
  fullName: string;
  phone: string;
  email: string;
  password: string;
  designation?: string;
}

export interface UpdateEmployeeInput {
  fullName?: string;
  phone?: string;
  email?: string;
  designation?: string;
}

function toPaged<T>(res: { data: Envelope<T[]> }): PagedResult<T> {
  return {
    items: res.data.data,
    page: res.data.meta!.page,
    limit: res.data.meta!.limit,
    total: res.data.meta!.total,
  };
}

export const employeeApi = {
  async listActive(): Promise<ActiveEmployee[]> {
    const res = await api.get<Envelope<ActiveEmployee[]>>("/employees/active");
    return res.data.data;
  },

  async list(params: {
    page: number;
    limit: number;
    search?: string;
    status?: string;
  }): Promise<PagedResult<Employee>> {
    const res = await api.get<Envelope<Employee[]>>("/employees", { params });
    return toPaged(res);
  },

  async getById(id: string): Promise<Employee> {
    const res = await api.get<Envelope<Employee>>(`/employees/${id}`);
    return res.data.data;
  },

  async getJobs(id: string, params: { page: number; limit: number }): Promise<PagedResult<EmployeeJob>> {
    const res = await api.get<Envelope<EmployeeJob[]>>(`/employees/${id}/jobs`, { params });
    return toPaged(res);
  },

  async create(input: CreateEmployeeInput): Promise<Employee> {
    const res = await api.post<Envelope<Employee>>("/employees", input);
    return res.data.data;
  },

  async update(id: string, input: UpdateEmployeeInput): Promise<Employee> {
    const res = await api.put<Envelope<Employee>>(`/employees/${id}`, input);
    return res.data.data;
  },

  async updateStatus(id: string, status: "ACTIVE" | "INACTIVE" | "ON_LEAVE"): Promise<Employee> {
    const res = await api.patch<Envelope<Employee>>(`/employees/${id}/status`, { status });
    return res.data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/employees/${id}`);
  },
};
